import React, { useEffect, useRef } from 'react';
import {
  Animated,
  Image,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useAuthStackContext } from '@/contexts/auth-stack-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';

interface Props {
  welcomeScreenActive: boolean;
}

const MAX_CIRCLE_DIAMETER = 160;
const MIN_CIRCLE_DIAMETER = 80;

const WelcomeHeaderLarge: React.FC<Props> = ({ welcomeScreenActive }) => {
  const theme = useTheme();
  const { appLandingLogoImageUrl } = useAuthStackContext();
  const { height, width } = useWindowDimensions();

  const logoUrl =
    appLandingLogoImageUrl ?? theme.images.welcomeLogoUrl ?? undefined;
  const diameterLarge = Math.min(
    MAX_CIRCLE_DIAMETER,
    Math.max(MIN_CIRCLE_DIAMETER, Math.min(height, width) * 0.44),
  );

  const circleOpacityRef = useRef(
    new Animated.Value(!welcomeScreenActive ? 0 : 0.85),
  );
  const smallLogoOpacityRef = useRef(
    new Animated.Value(!welcomeScreenActive ? 1 : 0),
  );

  useEffect(() => {
    Animated.parallel([
      Animated.timing(circleOpacityRef.current, {
        duration: 500,
        toValue: welcomeScreenActive ? 0.85 : 0,
        useNativeDriver: false,
      }),
      Animated.timing(smallLogoOpacityRef.current, {
        delay: 400,
        duration: 100,
        toValue: welcomeScreenActive ? 0 : 1,
        useNativeDriver: false,
      }),
    ]).start();
  }, [welcomeScreenActive]);

  if (!welcomeScreenActive) {
    return (
      <Animated.View
        style={[
          styles.headerContainerLargeArticle,
          {
            opacity: smallLogoOpacityRef.current,
          },
        ]}
      >
        <Image
          alt=""
          resizeMode="cover"
          source={{ uri: logoUrl }}
          style={styles.logoImageLargeArticle}
        />
      </Animated.View>
    );
  }

  return (
    <View pointerEvents="none" style={styles.headerContainer}>
      <Animated.View style={styles.circleContainer}>
        <Animated.View
          style={[
            styles.circle,
            {
              height: diameterLarge,
              opacity: circleOpacityRef.current,
              width: diameterLarge,
            },
          ]}
        >
          <Image
            alt=""
            resizeMode="cover"
            source={{ uri: logoUrl }}
            style={styles.logoImage}
          />
        </Animated.View>
      </Animated.View>
    </View>
  );
};

export const WelcomeHeaderRegular: React.FC<Props> = ({
  welcomeScreenActive,
}) => {
  const theme = useTheme();
  const { appLandingLogoImageUrl } = useAuthStackContext();
  const { top } = useSafeAreaInsets();
  const { height, width } = useWindowDimensions();

  const logoUrl =
    appLandingLogoImageUrl ?? theme.images.welcomeLogoUrl ?? undefined;
  const diameterLarge = Math.min(
    MAX_CIRCLE_DIAMETER,
    Math.max(MIN_CIRCLE_DIAMETER, Math.min(height, width) * 0.44),
  );

  const diameterRef = useRef(
    new Animated.Value(
      welcomeScreenActive ? diameterLarge : MIN_CIRCLE_DIAMETER,
    ),
  );
  const marginTopRef = useRef(new Animated.Value(top));

  useEffect(() => {
    Animated.parallel([
      Animated.timing(diameterRef.current, {
        duration: 500,
        toValue: welcomeScreenActive ? diameterLarge : MIN_CIRCLE_DIAMETER,
        useNativeDriver: false,
      }),
      Animated.timing(marginTopRef.current, {
        duration: 500,
        toValue: top,
        useNativeDriver: false,
      }),
    ]).start();
  }, [diameterLarge, welcomeScreenActive, top]);

  return (
    <View pointerEvents="none" style={styles.headerContainer}>
      <Animated.View
        style={[
          styles.circleContainer,
          {
            marginTop: marginTopRef.current,
          },
        ]}
      >
        <Animated.View
          style={[
            styles.circle,
            {
              height: diameterRef.current,
              width: diameterRef.current,
            },
          ]}
        >
          <Image
            alt=""
            resizeMode="cover"
            source={{ uri: logoUrl }}
            style={styles.logoImage}
          />
        </Animated.View>
      </Animated.View>
    </View>
  );
};

export const WelcomeHeader: React.FC<Props> = ({ welcomeScreenActive }) => {
  const { isLargeLayout } = useLayoutContext();

  return isLargeLayout ? (
    <WelcomeHeaderLarge welcomeScreenActive={welcomeScreenActive} />
  ) : (
    <WelcomeHeaderRegular welcomeScreenActive={welcomeScreenActive} />
  );
};

const styles = StyleSheet.create({
  circle: {
    alignItems: 'center',
    borderRadius: 1000,
    justifyContent: 'center',
    maxHeight: MAX_CIRCLE_DIAMETER,
    maxWidth: MAX_CIRCLE_DIAMETER,
    minHeight: MIN_CIRCLE_DIAMETER,
    minWidth: MIN_CIRCLE_DIAMETER,
    opacity: 0.85,
    overflow: 'hidden',
  },
  circleContainer: {
    alignItems: 'center',
    left: 0,
    right: 0,
  },
  headerContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  headerContainerLargeArticle: {
    width: '100%',
  },
  logoImage: {
    height: '100%',
    width: '100%',
  },
  logoImageLargeArticle: {
    height: 36,
    width: 100,
  },
});
